import { Button, Loader } from '@aws-amplify/ui-react';
import { useContext, useEffect, useState } from 'react';
import { BsFileText } from 'react-icons/bs';
import { SiHubspot, SiSalesforce } from 'react-icons/si';
import { TbArrowRight } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';
import LoadingBars from '../../custom-components/LoadingBars';
import { SelectableBoxGrid } from '../../custom-components/SelectableBoxGrid';
import { usePermission } from '../../hooks/usePermission';
import { toastV4Error } from '../../services/edulead-v4';
import { eduleadV4 } from '../../services/edulead-v4-client';
import General from '../../utils/GeneralUtils';
import { ExportContext } from '../ExportDetail';
import { EXPORT_TARGETS } from './exportShapes';

export function ExportStepTarget() {
  const exportContext = useContext(ExportContext);
  const navigate = useNavigate();
  const [selectedTarget, setSelectedTarget] = useState<string | null>(null);
  const { hasPermission } = usePermission();

  const saving = exportContext?.isSaving ?? false;
  const setSaving = exportContext?.setIsSaving ?? (() => {});

  const integrationsStatus = exportContext?.integrationsStatus;
  const isConnected = (id: string) => !!integrationsStatus?.find(i => i.id === id)?.connected;
  const loading = exportContext?.integrationsStatusLoading ?? true;

  // Pre-populate selected target from existing export data
  useEffect(() => {
    if (exportContext?.exportData?.target) {
      setSelectedTarget(exportContext.exportData.target.toLowerCase());
    }
  }, [exportContext?.exportData?.target]);

  console.log('[ExportStepTarget] render', {
    hasContext: !!exportContext,
    loading,
    integrationsStatusLoading: exportContext?.integrationsStatusLoading,
    hasExportData: !!exportContext?.exportData
  });

  if (!exportContext || loading) {
    console.log('[ExportStepTarget] showing loading bars');
    return (
      <div className="content content-1200">
        <div className="export-step">
          <LoadingBars numberOfRows={5} />
        </div>
      </div>
    );
  }

  const isSelectable = ['TARGET_SELECTION', 'FIELDS_SELECTION', 'OPTIONS_SELECTION'].includes(exportContext.exportData?.status);
  const readOnly = exportContext.readOnly ?? false;
  const isContact = exportContext.exportData?.entity === 'CONTACT';

  // Permission checks
  const canExportToCrm = isContact
    ? hasPermission('export-people-crm:execute')
    : hasPermission('export-institutions-crm:execute');
  const canExportToFile = isContact
    ? hasPermission('export-people-file:execute')
    : hasPermission('export-institutions-file:execute');

  // Integration status checks
  const hubspotDisabled = !isConnected('hubspot') || !canExportToCrm;
  // Salesforce is disabled if not connected or no permission
  const salesforceDisabled = !isConnected('salesforce') || !canExportToCrm;
  // File exports disabled if no permission
  const fileDisabled = !canExportToFile;

  // When not selectable (read-only), disable boxes that aren't selected
  const isBoxDisabled = (id: string, integrationDisabled: boolean) => {
    if (integrationDisabled) return true;
    if (!isSelectable && selectedTarget !== id) return true;
    return false;
  };

  // Determine disabled reason for tooltips
  const getHubspotDisabledInfo = () => {
    if (!canExportToCrm) return 'You do not have permission to export to CRM.';
    if (!isConnected('hubspot')) return 'HubSpot is not connected. Go to Settings > HubSpot to connect.';
    return undefined;
  };

  const getSalesforceDisabledInfo = () => {
    if (!canExportToCrm) return 'You do not have permission to export to CRM.';
    if (!isConnected('salesforce')) return 'Salesforce is not connected. Go to Settings > Salesforce to connect.';
    return undefined;
  };

  const getFileDisabledInfo = () => {
    if (!canExportToFile) return 'You do not have permission to export to file.';
    return undefined;
  };

  const exportTargets = [
    { id: 'hubspot', label: 'HubSpot', icon: <SiHubspot />, iconColor: isBoxDisabled('hubspot', hubspotDisabled) ? 'var(--amplify-colors-neutral-40)' : 'var(--brand-hubspot)', iconSize: 60, disabled: isBoxDisabled('hubspot', hubspotDisabled), disabledInfo: getHubspotDisabledInfo() },
    { id: 'salesforce', label: 'Salesforce', icon: <SiSalesforce />, iconColor: isBoxDisabled('salesforce', salesforceDisabled) ? 'var(--amplify-colors-neutral-40)' : 'var(--brand-salesforce)', iconSize: 70, disabled: isBoxDisabled('salesforce', salesforceDisabled), disabledInfo: getSalesforceDisabledInfo() },
    { id: 'csv', label: 'CSV', icon: <BsFileText />, iconColor: isBoxDisabled('csv', fileDisabled) ? 'var(--amplify-colors-neutral-40)' : 'var(--amplify-colors-purple-60)', iconSize: 48, disabled: isBoxDisabled('csv', fileDisabled), disabledInfo: getFileDisabledInfo() },
    { id: 'json', label: 'JSON', icon: <BsFileText />, iconColor: isBoxDisabled('json', fileDisabled) ? 'var(--amplify-colors-neutral-40)' : 'var(--amplify-colors-purple-60)', iconSize: 48, disabled: isBoxDisabled('json', fileDisabled), disabledInfo: getFileDisabledInfo() },
  ];

  const handleContinue = async () => {
    if (!selectedTarget) return;

    setSaving(true);
    try {
      await eduleadV4.exports.update(
        { id: exportContext.exportId, update_mask: 'target', body: { target: EXPORT_TARGETS[selectedTarget.toUpperCase()] } },
        { team: exportContext.teamId }
      );
      await exportContext.refreshExport();
      navigate(`/exports/${exportContext.exportId}/fields`);
    } catch (error) {
      toastV4Error(error, 'Failed to save target');
      setSaving(false);
    }
  };

  const estimatedCount = exportContext.exportData?.estimated_count;
  const entityLabel = isContact
    ? (estimatedCount === 1 ? 'contact' : 'contacts')
    : (estimatedCount === 1 ? 'institution' : 'institutions');
  const savedSearchName = exportContext.exportData?.search_name;

  return (
    <div className="content content-1200">
      <div className="export-step">
        <div style={{ marginBottom: 20 }}>
          Choose where you want to export your {estimatedCount ? <><strong>{General.formatCompactNumber(estimatedCount)} {entityLabel}</strong></> : entityLabel}{savedSearchName ? <> from search <strong>{savedSearchName}</strong></> : ''}.
        </div>
        <SelectableBoxGrid
          options={exportTargets}
          value={selectedTarget}
          onChange={setSelectedTarget}
          isSelectable={isSelectable && !readOnly}
        />
      </div>
      {isSelectable && !readOnly && (
        <div className="export-step__actions">
          {saving && <Loader size="small" />}
          <Button variation="primary" size="small" onClick={handleContinue} isDisabled={!selectedTarget || saving}>
            Save and Continue<TbArrowRight style={{ marginLeft: 8 }} />
          </Button>
        </div>
      )}
    </div>
  );
}
